import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import type { ParentType } from "./useMilestones";

export interface Task {
  id: number;
  project_id?: string;
  initiative_id?: string;
  milestone_id: number | null;
  /** Parent task id when this is a subtask (one level deep). null = top-level. */
  parent_task_id: number | null;
  title: string;
  assignee: string | null;
  role_key: string | null;
  status: string;
  priority: string;
  start_date: string | null;
  end_date: string | null;
  est_hours: number;
  sort_order: number;
  notes: string | null;
  /** Explicit RYG override. NULL = not set; UI falls back to the
   *  date-derived tone (overdue / due soon / upcoming). */
  health: "green" | "yellow" | "red" | null;
  /** Free-form product tags ("Gen 1", "Gen 2", etc.). Empty array =
   *  untagged. The project's product list is emergent — derived at
   *  render time from the union of tags across the project's tasks. */
  products: string[];
  /** "Blocked by" — other tasks in the same plan this one depends on.
   *  Populated by the backend on read; written via the /blockers routes. */
  blockers?: TaskBlockerRef[];
  /** Inverse — tasks that depend on this one. Read-only; managed from
   *  the other side. */
  blocking?: TaskBlockerRef[];
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

/** A task on the other end of a dependency edge — carries just enough
 *  to render a chip (title + a slip-relevant tone) without a refetch. */
export interface TaskBlockerRef {
  dep_id: number;
  id: number;
  title: string;
  status: string | null;
  end_date: string | null;
}

const listBase = (_parent: ParentType) => "/tasks";
const idBase = (_parent: ParentType) => "/tasks/id";
const cacheKey = (parent: ParentType, parentId: string | null) =>
  ["tasks", parent, parentId] as const;

export function useTasks(parentId: string | null, parent: ParentType = "project") {
  return useQuery<Task[]>({
    queryKey: cacheKey(parent, parentId),
    queryFn: () => api.get(`${listBase(parent)}/${parentId}`).then((r) => r.data),
    enabled: !!parentId,
  });
}

export function useCreateTask(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Task>) =>
      api.post(`${listBase(parent)}/${parentId}`, payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

export function useUpdateTask(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<Task> & { id: number }) =>
      api.patch(`${idBase(parent)}/${id}`, fields).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

export function useCompleteTask(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) =>
      api.post(`${idBase(parent)}/${taskId}/complete`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

export function useDeleteTask(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) => api.delete(`${idBase(parent)}/${taskId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

/** Add a "blocked by" edge: `taskId` is blocked by `blockerTaskId`. Both
 *  tasks must belong to the same plan. */
export function useAddTaskBlocker(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, blockerTaskId }: { taskId: number; blockerTaskId: number }) =>
      api
        .post(`${idBase(parent)}/${taskId}/blockers`, { blocker_task_id: blockerTaskId })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

export function useRemoveTaskBlocker(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, blockerTaskId }: { taskId: number; blockerTaskId: number }) =>
      api
        .delete(`${idBase(parent)}/${taskId}/blockers/${blockerTaskId}`)
        .then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}
